import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { MakeOfferDialog } from "@/components/negotiations";
import {
  Loader2,
  CheckCircle,
  Phone,
  Mail,
  Lock,
  IndianRupee,
  MessageCircle,
  Send,
  AlertCircle,
  ArrowLeft,
  MessageSquare
} from "lucide-react";

export function JetBookingSidebar({
  listing,
  bookingOpen,
  setBookingOpen,
  user,
  children
}) {
  const [offerDialogOpen, setOfferDialogOpen] = useState(false);

  return (
    <div className="lg:col-span-1">
      <div className="sticky top-24 glass p-6 space-y-6">
        <div>
          <p className="text-sm text-white/40 mb-1">Price per seat</p>
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-black text-[#D4AF37]">
              {listing.price_per_seat.toLocaleString('en-IN')}
            </span>
            <span className="text-white/60">INR</span>
          </div>
        </div>
        
        <div className="pt-4 border-t border-white/10">
          <p className="text-sm text-white/40 mb-1">Available Seats</p>
          <p className="text-2xl font-bold">{listing.available_seats}</p>
        </div>

        {/* Contact Info - Locked */}
        <div className="pt-4 border-t border-white/10">
          <div className="flex items-center gap-2 text-white/40 mb-3">
            <Lock className="w-4 h-4" />
            <span className="text-sm">Contact hidden until payment</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
            <DialogTrigger asChild>
              <Button 
                size="lg" 
                className="w-full rounded-none btn-hover bg-[#D4AF37] text-black hover:bg-[#D4AF37]/90"
                data-testid="book-jet-btn"
              >
                Book Seats Now
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md bg-[#171717] border-white/10 text-white">
              {children}
            </DialogContent>
          </Dialog>

          {/* Make an Offer Button */}
          {user && user.user_type !== "partner" && (
            <Button 
              size="lg" 
              variant="outline"
              className="w-full rounded-none border-white/20 text-white hover:bg-white/10"
              onClick={() => setOfferDialogOpen(true)}
              data-testid="make-jet-offer-btn"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Make an Offer
            </Button>
          )}
        </div>

        <p className="text-xs text-center text-white/40">
          2% platform fee • Secure payment via Razorpay
        </p>
      </div>

      {/* Offer Dialog */}
      <MakeOfferDialog
        open={offerDialogOpen}
        onOpenChange={setOfferDialogOpen}
        listing={listing}
        listingType="jet"
        onSuccess={() => {
          // Could refresh or show negotiation status
        }}
      />
    </div>
  );
}

export function BookingFormStep({
  listing,
  bookingForm,
  setBookingForm,
  totalAmount,
  platformFee,
  bookingLoading,
  onSubmit
}) {
  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-white">Book Your Seats</DialogTitle>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4 py-4">
        <div className="space-y-2">
          <Label htmlFor="quantity" className="text-white">Number of Seats *</Label>
          <Input
            id="quantity"
            type="number"
            min="1"
            max={listing.available_seats}
            placeholder={`Max: ${listing.available_seats}`}
            value={bookingForm.quantity}
            onChange={(e) => setBookingForm({ ...bookingForm, quantity: e.target.value })}
            required
            className="rounded-none bg-white/10 border-white/10 text-white"
            data-testid="jet-booking-quantity"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="shipper_name" className="text-white">Your Name *</Label>
          <Input
            id="shipper_name"
            placeholder="Full name"
            value={bookingForm.shipper_name}
            onChange={(e) => setBookingForm({ ...bookingForm, shipper_name: e.target.value })}
            required
            className="rounded-none bg-white/10 border-white/10 text-white"
            data-testid="jet-booking-name"
          />
        </div>

        <div className="pt-4 border-t border-white/10 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-white/60">Subtotal ({bookingForm.quantity} seats × ₹{listing.price_per_seat.toLocaleString('en-IN')})</span>
            <span>₹{totalAmount.toLocaleString('en-IN')}</span>
          </div>
          <div className="flex justify-between text-sm text-white/40">
            <span>Platform fee (2%)</span>
            <span>₹{platformFee.toLocaleString('en-IN')}</span>
          </div>
          <div className="flex justify-between font-bold pt-2 border-t border-white/10">
            <span>Total</span>
            <span className="text-[#D4AF37]">₹{(totalAmount).toLocaleString('en-IN')}</span>
          </div>
        </div>

        <Button 
          type="submit" 
          className="w-full rounded-none btn-hover bg-[#D4AF37] text-black hover:bg-[#D4AF37]/90"
          disabled={bookingLoading}
          data-testid="continue-jet-payment"
        >
          {bookingLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            "Continue to Payment"
          )}
        </Button>
      </form>
    </>
  );
}

export function PaymentStep({ currentBooking, bookingLoading, onPayment }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-white">Complete Payment</DialogTitle>
      </DialogHeader>
      <div className="space-y-6 py-4">
        <div className="p-4 bg-white/5 border border-white/10">
          <div className="flex justify-between mb-2">
            <span className="text-white/60">Booking Amount</span>
            <span className="font-bold">₹{currentBooking.total_amount.toLocaleString('en-IN')}</span>
          </div>
          <div className="flex justify-between text-sm text-white/40">
            <span>Platform Fee (2%)</span>
            <span>₹{currentBooking.platform_fee.toLocaleString('en-IN')}</span>
          </div>
        </div>

        <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/20">
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-200">
            Operator contact details will be revealed after successful payment.
          </p>
        </div>

        <Button 
          onClick={onPayment}
          className="w-full rounded-none btn-hover bg-[#D4AF37] text-black hover:bg-[#D4AF37]/90"
          disabled={bookingLoading}
          data-testid="pay-jet-btn"
        >
          {bookingLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <IndianRupee className="w-4 h-4 mr-1" />
              Pay ₹{currentBooking.total_amount.toLocaleString('en-IN')}
            </>
          )}
        </Button>
      </div>
    </>
  );
}

export function SuccessStep({ currentBooking, user, onChat, onDashboard }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-white">Booking Confirmed!</DialogTitle>
      </DialogHeader>
      <div className="space-y-6 py-4">
        <div className="text-center">
          <CheckCircle className="w-16 h-16 mx-auto text-[#D4AF37] mb-4" />
          <p className="text-white/60 mb-4">
            Your seats are booked! Contact the operator:
          </p>
        </div>
        
        <div className="space-y-3 p-4 bg-white/5 border border-white/10">
          <div className="flex items-center gap-3">
            <Phone className="w-5 h-5 text-[#D4AF37]" />
            <a href={`tel:${currentBooking.partner_contact_phone}`} className="font-medium hover:text-[#D4AF37]">
              {currentBooking.partner_contact_phone || "Not provided"}
            </a>
          </div>
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-[#D4AF37]" />
            <a href={`mailto:${currentBooking.partner_contact_email}`} className="font-medium hover:text-[#D4AF37]">
              {currentBooking.partner_contact_email || "Not provided"}
            </a>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <Button 
            variant="outline"
            className="rounded-none border-white/20 text-white hover:bg-white/10" 
            onClick={onChat}
            data-testid="open-jet-chat-btn"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            Chat
          </Button>
          <Button 
            className="rounded-none bg-[#D4AF37] text-black hover:bg-[#D4AF37]/90" 
            onClick={onDashboard}
            data-testid="go-to-dashboard-btn"
          >
            Dashboard
          </Button>
        </div>
      </div>
    </>
  );
}

export function ChatStep({ messages, user, newMessage, setNewMessage, sendingMessage, onSend, onBack, messagesEndRef }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-white">Chat with Operator</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div className="h-64 overflow-y-auto border border-white/10 p-4 space-y-3 bg-black/30">
          {messages.length === 0 ? (
            <p className="text-center text-white/40 text-sm py-8">
              No messages yet. Start the conversation!
            </p>
          ) : (
            messages.map((msg) => (
              <div 
                key={msg.id}
                className={`flex ${msg.sender_id === user?.user_id ? "justify-end" : "justify-start"}`}
              >
                <div className={`max-w-[80%] p-3 ${
                  msg.sender_id === user?.user_id 
                    ? "bg-[#D4AF37] text-black" 
                    : "bg-white/10"
                }`}>
                  <p className="text-xs opacity-70 mb-1">{msg.sender_name}</p>
                  <p className="text-sm">{msg.content}</p>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="flex items-start gap-3 p-3 bg-amber-500/10 border border-amber-500/20 text-xs">
          <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
          <span className="text-amber-200">
            Contact sharing is disabled in chat.
          </span>
        </div>

        <form onSubmit={onSend} className="flex gap-2">
          <Input
            placeholder="Type a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            className="rounded-none bg-white/10 border-white/10 text-white"
            data-testid="jet-chat-input"
          />
          <Button 
            type="submit" 
            size="icon" 
            className="rounded-none bg-[#D4AF37] text-black"
            disabled={sendingMessage || !newMessage.trim()}
          >
            {sendingMessage ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </form>

        <Button 
          variant="outline"
          className="w-full rounded-none border-white/20 text-white hover:bg-white/10" 
          onClick={onBack}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>
    </>
  );
}
